import React, { useState, useRef, useEffect } from 'react';
import { TfiUppercase } from 'react-icons/tfi';

const ServiceCards = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  const containerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const styles = {
    container: {
      width: '100%',
      maxWidth: '1400px',
      backgroundColor: 'white',
      margin: '0 auto',
      padding: windowWidth <= 768 ? '24px 16px' : '32px',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(50px)',
      transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
    },
    header: {
      textAlign: 'center',
      marginBottom: windowWidth <= 768 ? '32px' : '40px',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
      transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
      transitionDelay: '0.2s'
    },
    subtitle: {
      color: '#414042',
      fontSize: windowWidth <= 768 ? '14px' : '16px',
      fontWeight: '400',
      lineHeight: windowWidth <= 768 ? '20px' : '24px',
      marginBottom: '8px',
      fontFamily: 'Inter, sans-serif'
    },
    title: {
      fontSize: windowWidth <= 768 ? '28px' : '50px',
      fontWeight: '600',
      lineHeight: windowWidth <= 768 ? '32px' : '40px',
      color: '#400684',
      marginTop: '30px',
      marginBottom: '20px',
      fontFamily: 'Oswald, sans-serif'
    },
    scrollContainer: {
      overflowX: 'auto',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      WebkitOverflowScrolling: 'touch'
    },
    cardsWrapper: {
      display: 'flex',
      gap: windowWidth <= 768 ? '16px' : '24px',
      paddingBottom: '16px'
    },
    card: (index) => ({
      position: 'relative',
      flexShrink: 0,
      width: windowWidth <= 768 ? '260px' : '300px',
      height: windowWidth <= 768 ? '260px' : '300px',
      borderRadius: '12px',
      overflow: 'hidden',
      cursor: 'pointer',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
      transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
      transitionDelay: `${0.2 + (index * 0.1)}s`
    }),
    cardImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    },
    overlay: (isHovered) => ({
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backdropFilter: isHovered ? 'blur(4px)' : 'none',
      background: isHovered 
        ? 'rgba(0, 0, 0, 0.4)'
        : 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
      transition: 'all 0.3s ease',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      padding: windowWidth <= 768 ? '20px' : '24px'
    }),
    icon: {
      width: windowWidth <= 768 ? '40px' : '48px',
      height: 'auto',
      marginBottom: windowWidth <= 768 ? '12px' : '16px',
      filter: 'brightness(0) invert(1)'
    },
    cardTitle: {
      color: 'white',
      fontSize: windowWidth <= 768 ? '18px' : '20px',
      fontWeight: '500',
      marginBottom: '8px',
      fontFamily: 'Oswald, sans-serif'
    },
    cardDescription: (isHovered) => ({
      color: 'white',
      fontSize: windowWidth <= 768 ? '13px' : '14px',
      lineHeight: '1.5',
      fontFamily: 'Inter,sans-serif',
      opacity: isHovered ? 1 : 0,
      maxHeight: isHovered ? '100px' : '0',
      transition: 'all 0.3s ease',
      overflow: 'hidden'
    }),
    arrow: {
      position: 'absolute',
      right: windowWidth <= 768 ? '20px' : '24px',
      bottom: windowWidth <= 768 ? '20px' : '24px',
      color: 'white',
      fontSize: windowWidth <= 768 ? '20px' : '24px'
    }
  };

  const services = [
    {
      id: 1,
      title: 'MANAGED IT & SECURITY',
      description: 'Protecting your digital assets with advanced security measures and constant monitoring.',
      image: '/security.jpg',
      icon: '/lock1.png'
    },
    {
      id: 2,
      title: 'DATA INTEGRATION',
      description: 'Seamless data integration services for your enterprise.',
      image: '/slider2.jpg',
      icon: '/server.png'
    },
    {
      id: 3,
      title: 'CLOUD INFRASTRUCTURE',
      description: 'Robust cloud infrastructure solutions for scalable operations.',
      image: '/cloud.jpg',
      icon: '/cloud.png'
    },
    {
      id: 4,
      title: 'CYBER SECURITY',
      description: 'Enhanced cybersecurity measures to protect your digital assets.',
      image: '/cyber1.jpg',
      icon: '/cyber_security.png'
    },
    {
      id: 5,
      title: 'ANALYTICS SOLUTIONS',
      description: 'Advanced analytics solutions to drive your business decisions.',
      image: '/analytics1.jpg',
      icon: '/analytics.png'
    },
    {
      id: 6,
      title: 'WAREHOUSE MANAGEMENT',
      description: 'Comprehensive warehouse management solutions for your business needs.',
      image: '/warehouse.jpg',
      icon: '/wareicon.png'
    }
  ];

  return (
    <div style={styles.container} ref={containerRef}>
      <div style={styles.header}>
        <h2 style={styles.title}>
          OPTIMIZED SERVICES
        </h2>
        <div style={styles.subtitle}>Our goal is to provide top-quality technology services that drive meaningful results.</div>
      </div>
      
      <div style={styles.scrollContainer}>
        <div style={styles.cardsWrapper}>
          {services.map((service, index) => (
            <div
              key={service.id}
              style={styles.card(index)}
              onMouseEnter={() => setHoveredCard(service.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <img 
                src={service.image} 
                alt={service.title} 
                style={styles.cardImage} 
              />
              <div style={styles.overlay(hoveredCard === service.id)}>
                <img 
                  src={service.icon} 
                  alt="" 
                  style={styles.icon} 
                />
                <h3 style={styles.cardTitle}>{service.title}</h3>
                <p style={styles.cardDescription(hoveredCard === service.id)}>
                  {service.description}
                </p>
                <span style={styles.arrow}>→</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <style jsx global>{`
        @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;700&display=swap');
        
        *::-webkit-scrollbar {
          display: none;
        }
        * {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default ServiceCards;